// src/pages/Subscribe.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../utils/api";
import { motion } from "framer-motion";
import { FaCreditCard, FaCrown, FaSpinner, FaInfoCircle } from "react-icons/fa";

/* =========================
  Helpers
========================= */
const normalizePhone = (v) => String(v || "").replace(/\D/g, "");
const isValidEgyptPhone = (phone) => /^01[0125][0-9]{8}$/.test(String(phone || ""));

/* =========================
  PaySky Loader (cube)
  - inject script once
  - wait for window.Lightbox.Checkout
========================= */
const PAYSKY_CUBE_SRC = "https://cube.paysky.io/?R=xLsqCa";

/**
 * Inject PaySky script once (even if Subscribe mounts multiple times)
 * NOTE: if domain not whitelisted, script may load but Lightbox stays undefined.
 */
function injectPaySkyScriptOnce() {
  if (document.querySelector(`script[data-paysky-cube="1"]`)) return;

  const s = document.createElement("script");
  s.src = PAYSKY_CUBE_SRC;
  s.async = true;
  s.defer = true;
  s.setAttribute("data-paysky-cube", "1");

  s.onload = () => console.log("✅ PaySky script loaded:", PAYSKY_CUBE_SRC);
  s.onerror = () => console.warn("❌ PaySky script failed to load:", PAYSKY_CUBE_SRC);

  document.body.appendChild(s);
}

/**
 * Wait until PaySky Lightbox API is ready.
 * @param {number} timeoutMs default 25000
 */
function waitForPaySky(timeoutMs = 25000) {
  return new Promise((resolve, reject) => {
    const start = Date.now();

    const tick = () => {
      if (window?.Lightbox?.Checkout) return resolve(true);

      if (Date.now() - start > timeoutMs) {
        return reject(new Error("PaySky SDK timeout: window.Lightbox.Checkout not available."));
      }
      setTimeout(tick, 200);
    };

    tick();
  });
}

/**
 * Friendly message for common PaySky "Lightbox undefined" scenario
 * (usually domain whitelist / merchant activation)
 */
function getPaySkyNotReadyMessage() {
  return (
    "⚠️ PaySky غير جاهز على هذا الدومين.\n" +
    "لو window.Lightbox = undefined غالباً السبب Whitelist من PaySky.\n" +
    "كلم PaySky واطلب تفعيل الدومين: https://elavvocato.cloud"
  );
}

export default function Subscribe() {
  const navigate = useNavigate();
  const location = useLocation();

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  const [processing, setProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [userData, setUserData] = useState(null);

  // ✅ from Login (expired/frozen)
  const [blockedInfo] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("auth_block") || "null");
    } catch {
      return null;
    }
  });

  // ✅ renew flow from navigate("/subscribe", { state: { renewPhone, renewPassword } })
  const renewPhone = normalizePhone(location?.state?.renewPhone);
  const renewPassword = location?.state?.renewPassword || "";

  // ✅ important: Renew flow should work even if blockedInfo missing
  const isRenewFlow = !!blockedInfo?.code || (!!renewPhone && !!renewPassword);

  const egyptPhoneRegex = useMemo(() => /^01[0125][0-9]{8}$/, []);

  // PaySky readiness
  const [sdkReady, setSdkReady] = useState(false);
  const sdkLoadingRef = useRef(false);

  // prevent double submit / double lightbox open
  const inFlightRef = useRef(false);




/* ===========================================================
   ✅ Auto verify if redirected back from PaySky
=========================================================== */
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("MerchantReference");

  if (!ref) return;

  (async () => {
    try {
      const r = await api.get(`/payment/verify?ref=${encodeURIComponent(ref)}`);

      if (r.data?.success) {
        alert("✅ تم الدفع وتسجيل حسابك بنجاح");

        localStorage.removeItem("pendingUserData");
        localStorage.removeItem("auth_block");

navigate("/login", { replace: true });
window.history.replaceState({}, document.title, "/subscribe");
      } else {
alert("✅ تم الدفع بنجاح 🎉\nيمكنك تسجيل الدخول الآن");
      }
    } catch (e) {
      console.error("Verify error:", e);
    }
  })();
}, [navigate]);








  /* ===========================================================
     ✅ Ensure PaySky SDK is loaded & ready (PRO)
  =========================================================== */
  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      try {
        injectPaySkyScriptOnce();

        // avoid multiple boot runs racing
        if (sdkLoadingRef.current) return;
        sdkLoadingRef.current = true;

        await waitForPaySky(25000);

        if (!cancelled) setSdkReady(true);
      } catch (e) {
        console.warn("❌ PaySky SDK not ready:", e?.message || e);
        if (!cancelled) setSdkReady(false);
      } finally {
        sdkLoadingRef.current = false;
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ===========================================================
     ✅ Load pending user data (Register -> Subscribe) ONLY if NOT renew flow
  =========================================================== */
  useEffect(() => {
    if (isRenewFlow) {
      setUserData(null);
      return;
    }

    const stored = localStorage.getItem("pendingUserData");
    if (!stored) {
      alert("⚠️ من فضلك قم بالتسجيل أولاً.");
      navigate("/register");
      return;
    }

    let parsed = null;
    try {
      parsed = JSON.parse(stored || "{}");
    } catch {
      parsed = null;
    }

    const cleanPhone = normalizePhone(parsed?.phone);

    if (!cleanPhone || !egyptPhoneRegex.test(cleanPhone)) {
      alert("⚠️ رقم الموبايل غير موجود أو غير صحيح. يرجى إعادة التسجيل.");
      localStorage.removeItem("pendingUserData");
      navigate("/register");
      return;
    }

    // ✅ keep same structure but normalize phone
    setUserData({ ...parsed, phone: cleanPhone });
  }, [navigate, isRenewFlow, egyptPhoneRegex]);

  /* ===========================================================
     ✅ Fetch subscription plans
  =========================================================== */
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await api.get("/settings/plans");
        setPlans(res.data || []);
      } catch (err) {
        console.error("❌ خطأ في تحميل الخطط:", err);
        alert("حدث خطأ أثناء تحميل خطط الاشتراك.");
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  /* ===========================================================
     ✅ PaySky lightbox wrapper (robust)
  =========================================================== */
  const openPaySky = useCallback(async ({ data, onSuccess }) => {
    try {
      // Ensure SDK ready NOW (in case user opened page and clicked quickly)
      if (!window?.Lightbox?.Checkout) {
        try {
          await waitForPaySky(15000);
        } catch (e) {
          console.warn("PaySky wait failed:", e?.message || e);
        }
      }

      if (!window?.Lightbox?.Checkout) {
        alert(getPaySkyNotReadyMessage());
        return;
      }
let completedOnce = false;
      // some PaySky builds expect "configure = {..}" as you wrote
      // NOTE: ReturnURL is optional; if PaySky ignores it, no harm.
      window.Lightbox.Checkout.configure = {
        MID: data.MID,
        TID: data.TID,
        AmountTrxn: data.AmountTrxn,
        SecureHash: data.SecureHash,
        MerchantReference: data.MerchantReference,
        TrxDateTime: data.TrxDateTime,
        ...(data?.returnUrl ? { ReturnURL: data.returnUrl } : {}),

        completeCallback: async () => {
  if (completedOnce) return;   // 🔒 prevent duplicate firing
  completedOnce = true;

  try {
    await onSuccess?.();
  } catch (e) {
    console.error("❌ PaySky completeCallback error:", e);
    alert("حدث خطأ بعد الدفع. حاول مرة أخرى.");
  }
},


        errorCallback: (error) => {
          console.error("❌ PaySky errorCallback:", error);
          alert("حدث خطأ أثناء الدفع. حاول مرة أخرى.");
        },

        cancelCallback: () => {
          alert("⚠️ تم إلغاء عملية الدفع من قبل المستخدم.");
        },
      };

      window.Lightbox.Checkout.showLightbox();
    } catch (e) {
      console.error("❌ openPaySky failed:", e);
      alert("⚠️ تعذر فتح نافذة الدفع. حاول مرة أخرى.");
    }
  }, []);

  /* ===========================================================
     ✅ Login after renew (same behavior you wanted)
  =========================================================== */
  const loginAfterRenew = useCallback(
    async ({ phone, password }) => {
      if (!phone || !password) {
        navigate("/login");
        return;
      }

      try {
        const loginRes = await api.post("/auth/login", { phone, password });
        const token = loginRes.data?.token;
        const role = loginRes.data?.role || loginRes.data?.user?.role || "";
        const userId =
          loginRes.data?.user?._id ||
          loginRes.data?.userId ||
          loginRes.data?.id ||
          loginRes.data?._id ||
          "";

        if (token) localStorage.setItem("token", token);
        if (role) localStorage.setItem("role", role);
        if (userId) localStorage.setItem("userId", userId);

        localStorage.removeItem("auth_block");
        navigate("/dashboard");
      } catch (e) {
        console.error("❌ loginAfterRenew failed:", e);
        navigate("/login");
      }
    },
    [navigate]
  );

  /* ===========================================================
     ✅ Main subscribe handler (Register Flow + Renew Flow)
  =========================================================== */
  const handleSubscribe = async (plan, e) => {
    e.preventDefault();

    if (!plan?._id) {
      alert("⚠️ برجاء اختيار خطة صحيحة.");
      return;
    }

    // prevent double-click / double requests
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      setProcessing(true);
      setSelectedPlan(plan);

      // ===================================================
      // ✅ RENEW FLOW (expired/frozen)
      // ===================================================
      if (isRenewFlow) {
        const renewP = normalizePhone(renewPhone || blockedInfo?.phone || "");
        const renewPass = renewPassword;

        if (!renewP || !isValidEgyptPhone(renewP) || !renewPass) {
          alert("⚠️ تجديد الاشتراك يحتاج رقم الموبايل وكلمة المرور.\nارجع لتسجيل الدخول واضغط تجديد الاشتراك.");
          navigate("/login");
          return;
        }

        // ✅ Free renew (no PaySky)
        if (Number(plan.price) === 0) {
          const r = await api.post("/payment/renew-free", {
            planId: plan._id,
            phone: renewP,
            password: renewPass,
          });

          alert(`✅ ${r.data?.message || "تم تجديد الاشتراك."}`);
          await loginAfterRenew({ phone: renewP, password: renewPass });
          return;
        }

        // ✅ Paid renew (PaySky)
        // Ensure SDK loaded (robust)
        if (!sdkReady) {
          try {
            await waitForPaySky(15000);
            setSdkReady(true);
          } catch {
            alert(getPaySkyNotReadyMessage());
            return;
          }
        }

        const r = await api.post("/payment/renew-init", {
          planId: plan._id,
          phone: renewP,
          password: renewPass,
        });

        const data = r?.data || {};

        await openPaySky({
          data,
          onSuccess: async () => {
            // ✅ confirm payment (local/manual)
            await api.post("/payment/manual-confirm", { ref: data.MerchantReference });

            alert("✅ تم تجديد الاشتراك بنجاح!");
            await loginAfterRenew({ phone: renewP, password: renewPass });
          },
        });

        return;
      }

      // ===================================================
      // ✅ REGISTER FLOW
      // ===================================================
      if (!userData) {
        alert("⚠️ يجب التسجيل أولاً قبل الاشتراك.");
        navigate("/register");
        return;
      }

      const cleanPhone = normalizePhone(userData?.phone);

      if (!cleanPhone || !egyptPhoneRegex.test(cleanPhone)) {
        alert("⚠️ رقم الموبايل غير صالح. يرجى إعادة التسجيل.");
        localStorage.removeItem("pendingUserData");
        navigate("/register");
        return;
      }

      // ✅ Free plan register
      if (Number(plan.price) === 0) {
        const res = await api.post("/payment/free", {
          name: userData.name,
          email: userData.email,
          password: userData.password,
          phone: cleanPhone,
          planId: plan._id,
          referredBy: userData?.referredBy || null,
        });

        alert(`✅ تم إنشاء الحساب المجاني بنجاح!\nخطة: ${res.data.planName}\nيمكنك تسجيل الدخول الآن.`);
        localStorage.removeItem("pendingUserData");
        localStorage.removeItem("auth_block");
        navigate("/login");
        return;
      }

      // ✅ Paid register
      if (!sdkReady) {
        try {
          await waitForPaySky(15000);
          setSdkReady(true);
        } catch {
          alert(getPaySkyNotReadyMessage());
          return;
        }
      }

      const res = await api.post("/payment/init", {
        name: userData.name,
        email: userData.email,
        password: userData.password,
        phone: cleanPhone,
        planId: plan._id,
        referredBy: userData?.referredBy || null,
      });

      const data = res?.data || {};

      await openPaySky({
        data,
        onSuccess: async () => {
          // ✅ manual confirm (local/testing)
          await api.post("/payment/manual-confirm", { ref: data.MerchantReference });

          // ✅ auto login after successful payment
          const loginRes = await api.post("/auth/login", {
            phone: cleanPhone,
            password: userData.password,
          });

          const token = loginRes.data?.token;

          if (token) {
            localStorage.setItem("token", token);
            localStorage.removeItem("pendingUserData");
            localStorage.removeItem("auth_block");

            alert("✅ تم الدفع وتفعيل الحساب وتسجيل الدخول بنجاح!");
            navigate("/dashboard");
            return;
          }

          alert("⚠️ تم الدفع بنجاح لكن لم يتم تسجيل الدخول تلقائيًا.");
          navigate("/login");
        },
      });
    } catch (err) {
      console.error("❌ Subscribe error:", err);
      alert(err?.response?.data?.message || "حدث خطأ أثناء تنفيذ العملية.");
    } finally {
      setProcessing(false);
      inFlightRef.current = false;
    }
  };

  /* ===========================================================
     ✅ Loading
  =========================================================== */
  if (loadingPlans) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900 text-yellow-400 text-xl">
        <FaSpinner className="animate-spin mr-2" /> جاري تحميل خطط الاشتراك...
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white relative px-6">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-800 via-gray-900 to-black opacity-90"></div>

      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 text-center max-w-5xl w-full bg-gray-800/70 p-10 rounded-2xl border border-yellow-500 shadow-2xl"
      >
        {blockedInfo?.code && (
          <div className="mb-6 p-4 rounded-xl border border-yellow-500 bg-yellow-500/10 text-yellow-200 text-sm flex items-start gap-3">
            <FaInfoCircle className="mt-1" />
            <div className="text-left">
              <div className="font-bold">
                {blockedInfo.code === "SUBSCRIPTION_EXPIRED" ? "⚠️ انتهى اشتراكك" : "🚫 حسابك متجمد"}
              </div>
              <div className="mt-1">{blockedInfo.message || "يرجى اختيار خطة وتجديد الاشتراك لإعادة تفعيل الحساب."}</div>
            </div>
          </div>
        )}

        <div className="flex flex-col items-center mb-6">
          <FaCrown className="text-yellow-400 text-6xl mb-3" />
          <h1 className="text-3xl font-bold text-yellow-400 mb-2">اختر خطة الاشتراك المناسبة لك</h1>
          <p className="text-gray-300">
            جميع المدفوعات آمنة ومشفرة عبر <span className="text-yellow-400 font-semibold">PaySky</span> 🔒
          </p>

          {/* SDK status hint */}
          <div className="mt-4 text-xs">
            {sdkReady ? (
              <span className="text-green-400">✅ PaySky جاهز</span>
            ) : (
              <span className="text-yellow-300">⏳ جاري تجهيز PaySky...</span>
            )}
          </div>

          {/* Helpful hint when paid plans exist but SDK isn't ready */}
          {!sdkReady && plans.some((p) => Number(p?.price) > 0) && (
            <div className="mt-2 text-xs text-yellow-200/90">
              لو الدفع المدفوع مش شغال و <span className="font-bold">window.Lightbox</span> = undefined
              → غالباً تحتاج تفعيل الدومين (Whitelist) من PaySky.
            </div>
          )}
        </div>

        {plans.length > 0 ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const isFree = Number(plan.price) === 0;
              const isPaid = !isFree;

              // ✅ Safety: prevent paid click if SDK not ready (avoid annoying alert)
              const canPayPaid = sdkReady;

              return (
                <motion.div
                  key={plan._id}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                  className={`rounded-2xl p-6 cursor-pointer border ${
                    selectedPlan?._id === plan._id ? "border-yellow-400 bg-yellow-100/10" : "border-gray-700 bg-gray-800/50"
                  } shadow-lg flex flex-col items-center justify-between`}
                  onClick={() => setSelectedPlan(plan)}
                >
                  <h2 className="text-xl font-bold text-yellow-400 mb-2">{plan.name}</h2>

                  <p className="text-gray-300 text-sm mb-2">
                    📅 المدة: <span className="font-semibold">{plan.durationDays}</span> يوم
                  </p>

                  <p className="text-yellow-400 text-2xl font-bold mb-2">{isFree ? "مجانًا 🎁" : `${plan.price} جنيه`}</p>

                  {!!plan.trialDays && <p className="text-green-400 text-sm mb-3">🎁 {plan.trialDays} يوم مجاني تجريبي</p>}

                  <button
                    type="button"
                    onClick={(e) => {
                      // paid plan + sdk not ready => show clear message without triggering flow
                      if (isPaid && !canPayPaid && !processing) {
                        e.preventDefault();
                        alert(getPaySkyNotReadyMessage());
                        return;
                      }
                      handleSubscribe(plan, e);
                    }}
                    disabled={processing}
                    className={`w-full py-2 mt-3 rounded-lg font-bold transition ${
                      processing
                        ? "bg-gray-600 text-gray-300 cursor-not-allowed"
                        : isFree
                        ? "bg-green-500 hover:bg-green-600 text-black"
                        : canPayPaid
                        ? "bg-yellow-500 hover:bg-yellow-600 text-black"
                        : "bg-yellow-500/40 text-black/60 cursor-not-allowed"
                    }`}
                    title={isPaid && !canPayPaid ? "PaySky غير جاهز (Whitelist)" : ""}
                  >
                    {processing ? (
                      <span className="flex items-center justify-center gap-2">
                        <FaSpinner className="animate-spin" /> جاري المعالجة...
                      </span>
                    ) : isRenewFlow ? (
                      isFree ? (
                        "🔁 جدّد مجانًا"
                      ) : (
                        <>
                          <FaCreditCard className="inline mr-2" /> جدّد الاشتراك
                        </>
                      )
                    ) : isFree ? (
                      "ابدأ مجانًا الآن 🚀"
                    ) : (
                      <>
                        <FaCreditCard className="inline mr-2" /> اشترك الآن
                      </>
                    )}
                  </button>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-400 mt-6">📭 لا توجد خطط اشتراك متاحة حالياً.</p>
        )}


{/* ======================================
   Manual Contact Fallback
====================================== */}
<div className="mt-8 text-center border-t border-gray-700 pt-6">

  <p className="text-gray-300 mb-3">
    واجهت مشكلة أثناء الاشتراك؟
  </p>

  <a
    href="https://wa.me/201017073884?text=مرحباً،%20أرغب%20في%20الاشتراك%20يدوياً"
    target="_blank"
    rel="noopener noreferrer"
    className="inline-block bg-green-500 hover:bg-green-600 text-black font-bold py-2 px-6 rounded-lg transition"
  >
    تواصل مع الإدارة عبر واتساب
  </a>

</div>


      </motion.div>
    </div>
  );
}

